import React, { useState } from "react";
import { CircleAlert, CircleCheck, RefreshCw } from "lucide-react";
import customFetch from "./apicall/api";
import ReportHeader from "./Report_header";
import "./LoanDeviation.css";

const formatLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^\w/, (character) => character.toUpperCase());

function ResponseValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="loan-deviation-empty">Not provided</span>;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.length ? value.map((item) => String(item)).join(", ") : "None";
  }

  if (typeof value === "object") {
    return (
      <dl className="loan-deviation-nested-details">
        {Object.entries(value).map(([key, nestedValue]) => (
          <div key={key}>
            <dt>{formatLabel(key)}</dt>
            <dd><ResponseValue value={nestedValue} /></dd>
          </div>
        ))}
      </dl>
    );
  }

  return String(value);
}

export default function LoanDeviation({ history }) {
  const [loanDocumentationId, setLoanDocumentationId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    const loanId = loanDocumentationId.trim();
    if (!loanId) {
      setError("Loan documentation ID is required.");
      setResult(null);
      return;
    }

    setIsSubmitting(true);
    setError("");
    setResult(null);

    try {
      const response = await customFetch(
        `${process.env.REACT_APP_URL}/check-loan-deviation`,
        {
          method: "POST",
          headers: {
            "api-token": localStorage.getItem("api_token"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ loan_documentation_id: loanId }),
        }
      );
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Request failed (${response.status}).`);
      }

      setResult(responseData);
    } catch (requestError) {
      setError(requestError.message || "Unable to update loan demand details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="loan-deviation-page">
      <ReportHeader
        title="Loan Deviation"
        showBack
        onBack={() => history.goBack()}
      />
      <main className="loan-deviation-content">
        <section className="loan-deviation-panel" aria-labelledby="loan-deviation-title">
          <div className="loan-deviation-heading">
            <span className="loan-deviation-icon" aria-hidden="true">
              <RefreshCw size={24} strokeWidth={1.8} />
            </span>
            <div>
              <h1 id="loan-deviation-title">Update loan demand details</h1>
              <p>Run the loan deviation check for a specific loan.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <label htmlFor="loan-documentation-id">Loan documentation ID</label>
            <input
              id="loan-documentation-id"
              className="form-control"
              type="text"
              value={loanDocumentationId}
              onChange={(event) => setLoanDocumentationId(event.target.value)}
              placeholder="Enter loan documentation ID"
              autoComplete="off"
              required
            />
            <button
              className="loan-deviation-submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Check & Update"}
            </button>
          </form>

          {error && (
            <div className="loan-deviation-feedback is-error" role="alert">
              {error}
            </div>
          )}

          {result && (
            <section
              className={`loan-deviation-result${result.status === "FAILED" ? " is-error" : ""}`}
              aria-live="polite"
            >
              <div className="loan-deviation-result-heading">
                <span className="loan-deviation-result-icon" aria-hidden="true">
                  {result.status === "FAILED" ? <CircleAlert size={21} /> : <CircleCheck size={21} />}
                </span>
                <div>
                  <h2>
                    {result.status === "FAILED"
                      ? "Update failed"
                      : result.deviation_found === true
                        ? "Deviation found"
                        : result.deviation_found === false
                          ? "No deviations found"
                          : "Update complete"}
                  </h2>
                  {result.message && <p>{result.message}</p>}
                </div>
                {result.status && (
                  <span className="loan-deviation-status">{formatLabel(result.status.toLowerCase())}</span>
                )}
              </div>
              <dl className="loan-deviation-details">
                {Object.entries(result)
                  .filter(([key]) => !["message", "status", "deviation_found"].includes(key))
                  .map(([key, value]) => (
                    <div className="loan-deviation-detail" key={key}>
                      <dt>{formatLabel(key)}</dt>
                      <dd><ResponseValue value={value} /></dd>
                    </div>
                  ))}
              </dl>
            </section>
          )}
        </section>
      </main>
    </div>
  );
}